import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { Home, FileText, UserCheck, Shield } from "lucide-react";

export default function RoleSelectionPage() {
  const { token, updateUser, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);

  const roles = [
    {
      id: "applicant",
      title: "I want to complete Application for Tenancy",
      description: "Apply to become a tenant at WilyRash Homes",
      icon: FileText,
      color: "bg-blue-100 text-blue-600"
    },
    {
      id: "tenant",
      title: "I am a Tenant",
      description: "I am already an approved tenant and want to update my information",
      icon: UserCheck,
      color: "bg-green-100 text-green-600"
    },
    {
      id: "manager",
      title: "I am a Manager",
      description: "I am a property manager (requires admin approval)",
      icon: Shield,
      color: "bg-purple-100 text-purple-600"
    }
  ];

  const handleRoleSelect = async (roleId) => {
    setSelectedRole(roleId);
    setLoading(true);
    
    try {
      await axios.post(
        `${API}/auth/select-role`,
        { user_type: roleId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local user state
      updateUser({ ...user, user_type: roleId, has_selected_role: true });
      
      // Navigate based on role
      if (roleId === "applicant") {
        navigate("/new-application");
      } else if (roleId === "tenant") {
        navigate("/tenant-info-update");
      } else if (roleId === "manager") {
        toast.info("Your manager request has been submitted. Please wait for admin approval.");
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to select role");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4" data-testid="role-selection-page">
      <div className="w-full max-w-2xl animate-fade-in">
        {/* Logo/Brand */}
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center">
            <Home className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-primary">WilyRash Homes</h1>
            <p className="text-sm text-text-muted">Tenant Management System</p>
          </div>
        </div>

        <Card className="border-stone-200 shadow-lg">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-semibold">Welcome, {user?.full_name}!</CardTitle>
            <CardDescription className="text-base">
              Please select an option to continue
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {roles.map((role) => (
                <button
                  key={role.id}
                  onClick={() => handleRoleSelect(role.id)}
                  disabled={loading}
                  className={`w-full p-6 rounded-xl border-2 transition-all text-left ${
                    selectedRole === role.id
                      ? "border-primary bg-primary/5"
                      : "border-stone-200 hover:border-primary/50 hover:bg-stone-50"
                  } ${loading ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                  data-testid={`role-option-${role.id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl ${role.color} flex items-center justify-center flex-shrink-0`}>
                      <role.icon className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-primary text-lg">{role.title}</h3>
                      <p className="text-sm text-text-muted mt-1">{role.description}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {loading && (
              <div className="mt-6 text-center">
                <p className="text-sm text-text-muted animate-pulse">Processing your selection...</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
